import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  Renderer2,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, RouteConfigLoadEnd, Router } from '@angular/router';

import { HeaderWrapperService } from '@amfam/shared/ui/ds-header/lib/services/header-wrapper.service';

import { TabFocusDirective } from '../../../accessibility/tab-focus.directive';
import { DsButtonComponent } from '../../../ds-button/components/ds-button/ds-button.component';
import { PageTitleService } from '../../../page-title/page-title.service';
import { Action } from '../../models/action.model';
import { DockingBarService } from '../../services/docking-bar.service';

@Component({
  selector: 'ds-docking-bar',
  templateUrl: './docking-bar.component.html',
  styleUrls: ['./docking-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabFocusDirective, DsButtonComponent]
})
export class DockingBarComponent implements OnInit {
  private dockService = inject(DockingBarService);
  private pageTitle = inject(PageTitleService);
  private router = inject(Router);
  private headerWrapperService = inject(HeaderWrapperService);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  readonly isShellAccount = input<boolean | null>(false);
  readonly mainHeading = viewChild.required<ElementRef>('mainHeading');
  readonly dockingBar = viewChild.required<ElementRef>('dockingBar');

  readonly actions = signal<Action[]>([]);
  readonly heading = signal<string | null>('My Account');
  readonly secondaryHeading = signal<string | null>(null);
  readonly empty = signal(true);
  readonly routerUrl = signal('');

  ngOnInit() {
    // Subscribe to main content focus event
    this.headerWrapperService.focusMainContent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const mainHeading = this.mainHeading();
        mainHeading.nativeElement.tabIndex = 0;
        mainHeading.nativeElement.focus();
      });

    // filter only for NavigationEnd and RouteConfigLoadEnd (app load) events and set routerUrl var
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof RouteConfigLoadEnd) {
        this.routerUrl.set(this.router.url);
      }

      // filter only for NavigationStart events
      // we want this code to run _before_ the ngOnInit of the next page
      if (!(event instanceof NavigationStart)) {
        return;
      }

      // don't do anything if we're navigating to the same page
      if (event.url === this.router.url) {
        return;
      }

      this.dockService.clear();
    });

    this.dockService.dockHeading$.subscribe(heading => this.updateHeading(heading));
    this.dockService.dockSecondaryHeading$.subscribe(heading =>
      this.updateSecondaryHeading(heading)
    );
    this.dockService.dockActions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(actions => {
      // The service always emits a new array, so the signal is notified on every change.
      this.actions.set(actions.sort((a, b) => (a.title > b.title ? -1 : 1)));
    });

    this.pageTitle.title$.subscribe(heading => this.updatePageTitle(heading));
  }

  private updateHeading(heading: string | null) {
    this.heading.set(heading);

    if (this.isShellAccount() === undefined) {
      this.renderer.removeClass(this.dockingBar().nativeElement, 'primary-800-bg');
      this.renderer.addClass(this.dockingBar().nativeElement, 'docking-bar-amfam');
    }

    this.empty.set(!heading);
  }

  private updateSecondaryHeading(secondaryHeading: string | null) {
    this.secondaryHeading.set(secondaryHeading);
  }

  private updatePageTitle(heading: string) {
    // if the page has registered a dock heading, it takes priority
    if (!this.heading()) {
      this.dockService.registerHeading(heading);
    }
  }
}
